import { buildLocalizedOfferContent } from '../appRouteOfferCore.js';
import APP_ROUTE_OFFER_COPY_PT from './appRouteOfferContent.pt.js';

// Only this locale's offer copy: appRouteSeo.js imports some of these
// content files into the shared client chunk.
const OFFER_CONTENT = buildLocalizedOfferContent(APP_ROUTE_OFFER_COPY_PT, 'pt');

export const APP_ROUTE_SEO_CONTENT_PT = {
  "/metadata": {
    "title": "Metadata Studio",
    "intro": [
      "Gere palavras-chave, títulos e descrições de fotos, vetores e vídeos em um fluxo de trabalho focado. Stocktag funciona tanto como uma ferramenta de imagem para palavra-chave para execuções rápidas quanto como um estúdio completo de metadados para trabalho de produção.",
      "Faça upload de ativos, revise resultados rapidamente e exporte CSV/XMP sem perder consistência em seu portfólio."
    ],
    "bullets": [
      "Fluxo de trabalho de imagem para palavra-chave, título e descrição em uma única passagem",
      "Ferramentas de revisão rápida para limpar duplicatas e tags fracas",
      "Resultados prontos para exportação para os principais bancos de imagens"
    ],
    "links": [
      {
        "href": "/pricing",
        "label": "Ver planos de preços"
      },
      {
        "href": "/blog",
        "label": "Leia o blog de metadados"
      },
      {
        "href": "/free-credits",
        "label": "Obtenha créditos grátis"
      }
    ],
    "sections": [
      {
        "title": "Visão geral do fluxo de trabalho",
        "paragraphs": [
          "Carregue um lote, gere títulos, descrições e palavras-chave de uma só vez e, em seguida, revise e exporte sem perder o contexto do arquivo."
        ],
        "bullets": [
          "Carregar e processar lotes com nomes de arquivo limpos",
          "Revise a ordem das palavras-chave, o texto do título e as tags fracas",
          "Exporte CSV ou XMP para upload no marketplace"
        ]
      },
      {
        "title": "Para quem é",
        "paragraphs": [
          "Projetado para colaboradores, estúdios e equipes que publicam em grande escala e precisam de um fluxo de trabalho confiável de títulos e palavras-chave stock em todos os mercados."
        ],
        "bullets": [
          "Criadores gerenciando grandes bibliotecas",
          "Equipes que precisam de regras de metadados repetíveis",
          "Publicação de portfólios em vários sites"
        ]
      }
    ],
    "faq": [
      OFFER_CONTENT.creditUsageFaq,
      {
        "q": "Quais mercados são suportados?",
        "a": "Os principais bancos de imagens como Adobe Stock, Shutterstock, Pond5, Getty e mais são suportados."
      }
    ]
  },
  "/market-intelligence": {
    "title": "Market Intelligence",
    "intro": [
      "Pesquise tendências de palavras-chave stock e a demanda sazonal. O Trends Radar lê resultados e sugestões do Google e buscas em sites stock como Adobe Stock e Shutterstock.",
      "Combine insights com Metadata Studio para publicar mais rapidamente e classificar com intenção de pesquisa mais clara."
    ],
    "bullets": [
      "Identifique mudanças de tendência nas pesquisas dos compradores",
      "Siga um calendário microstock para planejamento sazonal",
      "Identifique lacunas de conteúdo por categoria",
      "Planeje a produção com sinais de demanda real"
    ],
    "links": [
      {
        "href": "/metadata",
        "label": "Abra Metadata Studio"
      },
      {
        "href": "/pricing",
        "label": "Comparar planos"
      },
      {
        "href": "/blog",
        "label": "Explore guias de tendências"
      }
    ],
    "sections": [
      {
        "title": "Encontre a demanda antes de filmar",
        "paragraphs": [
          "O Trends Radar mostra como o interesse de busca por um tema está mudando, com base em resultados e sugestões do Google e em resultados de busca nos sites stock, para você planejar a produção com sinais de demanda mais claros."
        ],
        "bullets": [
          "Acompanhe as mudanças sazonais na demanda",
          "Identifique palavras-chave de alta intenção com antecedência",
          "Valide conceitos antes da produção"
        ]
      },
      {
        "title": "Transforme insights em metadados",
        "paragraphs": [
          "Use sinais de tendência e planejamento de calendário para atualizar conjuntos de palavras-chave e alinhar títulos e descrições com a intenção real do comprador."
        ],
        "bullets": [
          "Atualize portfólios antigos com novos termos",
          "Alinhe metadados com sinais de demanda",
          "Envie mais rápido com modelos reutilizáveis"
        ]
      }
    ],
    "faq": [
      {
        "q": "De onde vêm os dados de tendências?",
        "a": "O Trends Radar estima a demanda a partir de sinais públicos de busca: resultados e sugestões do Google para um tema e resultados de busca nos principais sites stock, como Adobe Stock, Shutterstock, iStock e Getty Images. Ele não usa dados internos de busca dos marketplaces."
      },
      {
        "q": "Com que frequência as tendências são atualizadas?",
        "a": "Os instantâneos de tendências são atualizados regularmente à medida que novos sinais de demanda aparecem."
      },
      {
        "q": "Isso inclui um calendário microstock?",
        "a": "Sim. Market Intelligence inclui visualizações de planejamento sazonal e de feriados para que você possa mapear oportunidades de palavras-chave para a janela de produção correta."
      },
      {
        "q": "Isso está incluído em todos os planos?",
        "a": "O Holiday Planner está disponível para todas as contas conectadas. O Trends Radar e o Similarity Scanner estão incluídos nos planos mensais de créditos."
      }
    ]
  },
  "/free-credits": OFFER_CONTENT.freeCredits,
  "/image-generator": {
    "title": "Image Generator",
    "intro": [
      "Gere recursos visuais de referência para fluxos de trabalho para bancos de imagens, testes de conceito e idealização de palavras-chave para imagens. Combine saídas de imagem com metadados para validar a intenção de pesquisa com mais rapidez.",
      "Mantenha seu pipeline de produção em movimento enquanto testa estilos, composições e casos de uso."
    ],
    "bullets": [
      "Validação rápida de conceito",
      "Teste de ideia de palavra-chave para imagem",
      "Exploração de estilo para ativos prontos para bancos de imagens",
      "Resultados amigáveis ao fluxo de trabalho para planejamento"
    ],
    "links": [
      {
        "href": "/metadata",
        "label": "Gerar metadados"
      },
      {
        "href": "/pricing",
        "label": "Ver planos"
      },
      {
        "href": "/blog",
        "label": "Leia dicas de produção"
      }
    ],
    "sections": [
      {
        "title": "Casos de uso para planejamento stock",
        "paragraphs": [
          "Gere referências visuais rápidas para explorar composição, estilo, conceitos sazonais ou sugestões de palavras-chave antes de uma filmagem completa."
        ],
        "bullets": [
          "Teste de conceito e moodboards",
          "Exploração de estilo para novas coleções",
          "Suporte ao planejamento de pré-produção"
        ]
      },
      {
        "title": "Parear com metadados",
        "paragraphs": [
          "Use os conceitos gerados para passar de ideias de palavra-chave para imagem para metadados de imagem para palavra-chave em Metadata Studio para que a produção permaneça alinhada."
        ],
        "bullets": [
          "Alinhe as solicitações com a intenção da palavra-chave",
          "Economize tempo na escrita de pré-produção",
          "Acelere a preparação para publicação"
        ]
      }
    ],
    "faq": [
      {
        "q": "Ele gera imagens prontas para bancos de imagens?",
        "a": "Produz imagens de referência. Siga as políticas da plataforma para envios finais."
      },
      {
        "q": "Posso exportar as saídas?",
        "a": "Sim. Baixe resultados para planejamento e preparação de metadados."
      }
    ]
  },
  "/vectorizer": {
    "title": "AI Vectorizer",
    "intro": [
      "Converta imagens raster em vetores SVG limpos para listagens stock. Reduza o tempo de rastreamento manual e mantenha a qualidade da linha consistente.",
      "Use resultados vetoriais junto com metadados para enviar ativos prontos para o mercado com mais rapidez."
    ],
    "bullets": [
      "Conversão rápida de raster para vetor",
      "Caminhos limpos para ativos escaláveis",
      "Fluxo de trabalho amigável em lote"
    ],
    "links": [
      {
        "href": "/metadata",
        "label": "Abra Metadata Studio"
      },
      {
        "href": "/pricing",
        "label": "Comparar planos"
      },
      {
        "href": "/blog",
        "label": "Dicas de fluxo de trabalho vetorial"
      }
    ],
    "sections": [
      {
        "title": "Saída vetorial limpa",
        "paragraphs": [
          "Converta arte raster em ativos prontos para SVG, preservando a qualidade das linhas e bordas."
        ],
        "bullets": [
          "Reduza o tempo de rastreamento manual",
          "Mantenha resultados escalonáveis para listagens",
          "Mantenha caminhos limpos"
        ]
      },
      {
        "title": "Ajuste ao fluxo de trabalho",
        "paragraphs": [
          "Use saídas vetoriais com Metadata Studio para gerar títulos e palavras-chave na mesma sessão."
        ],
        "bullets": [
          "Conversões amigáveis em lote",
          "Nomenclatura de arquivo consistente",
          "Pipeline pronto para exportação"
        ]
      }
    ],
    "faq": [
      {
        "q": "Quais formatos são suportados?",
        "a": "Entradas raster comuns são suportadas com saídas prontas para SVG para envios para bancos de imagens."
      },
      {
        "q": "O processamento em lote está disponível?",
        "a": OFFER_CONTENT.vectorizerAllowance
      }
    ]
  },
  "/prompter": {
    "title": "Prompter",
    "intro": [
      "Crie prompts estruturados para geração consistente de ativos e planejamento de metadados. Use modelos de prompt para manter a saída limpa e reutilizável.",
      "Combine prompts com Metadata Studio para produzir títulos e palavras-chave que correspondam à sua intenção criativa."
    ],
    "bullets": [
      "Estrutura de prompt baseada em modelo",
      "Bibliotecas de prompt reutilizáveis",
      "Iteração mais rápida para conceitos stock"
    ],
    "links": [
      {
        "href": "/metadata",
        "label": "Gerar metadados"
      },
      {
        "href": "/pricing",
        "label": "Veja opções de planos"
      },
      {
        "href": "/help",
        "label": "Guia Prompter"
      }
    ],
    "sections": [
      {
        "title": "Design de prompt estruturado",
        "paragraphs": [
          "Prompter ajuda a criar prompts repetíveis com variáveis para que suas saídas permaneçam consistentes."
        ],
        "bullets": [
          "Estrutura de prompt baseada em modelo",
          "Bibliotecas de prompt reutilizáveis",
          "Iteração mais rápida para novos conceitos"
        ]
      },
      {
        "title": "Integrar com metadados",
        "paragraphs": [
          "Use suas bibliotecas de prompts para manter a linguagem de metadados alinhada com sua intenção criativa."
        ],
        "bullets": [
          "Conjuntos de palavras-chave mais limpos",
          "Melhor nomenclatura de conceito",
          "Descrições consistentes"
        ]
      }
    ],
    "faq": [
      {
        "q": "Posso salvar modelos de prompt?",
        "a": "Sim. Os modelos podem ser reutilizados em projetos e equipes."
      },
      {
        "q": "Funciona com Metadata Studio?",
        "a": "Use prompts junto com a geração de metadados para manter os resultados alinhados."
      }
    ]
  },
  "/pricing": OFFER_CONTENT.pricing,
  "/help": {
    "title": "Central de Ajuda",
    "intro": [
      "Encontre guias de fluxo de trabalho, instruções de configuração e etapas de solução de problemas. A central de ajuda analisa metadados, exportações e uso do plano.",
      "Use o changelog para acompanhar atualizações e melhorias recentes."
    ],
    "bullets": [
      "Tutoriais de fluxo de trabalho de início rápido",
      "Guias de exportação e configuração de plataforma",
      "Notas de versão atualizadas"
    ],
    "links": [
      {
        "href": "/help/changelog",
        "label": "Ver registro de alterações"
      },
      {
        "href": "/metadata",
        "label": "Abra Metadata Studio"
      },
      {
        "href": "/blog",
        "label": "Leia dicas de fluxo de trabalho"
      }
    ],
    "sections": [
      {
        "title": "O que você aprenderá",
        "paragraphs": [
          "Os guias abrangem geração de metadados, exportações, predefinições de plataforma e solução de problemas."
        ],
        "bullets": [
          "Configuração do fluxo de trabalho de metadados",
          "Exportar formatos e uploads",
          "Noções básicas de conta e faturamento"
        ]
      },
      {
        "title": "Pontos de partida populares",
        "paragraphs": [
          "Comece com o guia Metadata Studio e revise o changelog para obter as atualizações mais recentes."
        ],
        "bullets": [
          "Passo a passo de Metadata Studio",
          "Dicas de exportação de plataforma",
          "Notas de lançamento e atualização"
        ]
      }
    ],
    "faq": [
      {
        "q": "Por onde devo começar?",
        "a": "Comece com o guia Metadata Studio e siga o fluxo de trabalho passo a passo."
      },
      {
        "q": "Precisa de suporte?",
        "a": "Use a Central de Ajuda ou entre em contato com a equipe dentro do aplicativo."
      }
    ]
  },
  "/help/changelog": {
    "title": "Registro de alterações",
    "intro": [
      "Acompanhe melhorias e atualizações recentes em Stocktag. Cada versão destaca novos recursos, correções e melhorias no fluxo de trabalho.",
      "Verifique a central de ajuda para obter guias mais detalhados sobre o uso de novos recursos."
    ],
    "bullets": [
      "Atualizações recentes de produtos",
      "Melhorias e correções de recursos",
      "Melhorias no fluxo de trabalho"
    ],
    "links": [
      {
        "href": "/help",
        "label": "Voltar à Central de Ajuda"
      },
      {
        "href": "/blog",
        "label": "Leia as atualizações do blog"
      },
      {
        "href": "/metadata",
        "label": "Abra Metadata Studio"
      }
    ],
    "sections": [
      {
        "title": "Acompanhar lançamentos",
        "paragraphs": [
          "Cada entrada lista novos recursos, correções e atualizações de fluxo de trabalho para que você possa se manter atualizado."
        ],
        "bullets": [
          "Melhorias no produto",
          "Melhorias no fluxo de trabalho",
          "Correções de estabilidade e desempenho"
        ]
      },
      {
        "title": "Mantenha-se informado",
        "paragraphs": [
          "Revise as alterações recentes antes de atualizar seu fluxo de trabalho ou integrar novos membros da equipe."
        ],
        "bullets": [
          "Compartilhe atualizações com sua equipe",
          "Verifique se há novas opções de exportação",
          "Planeje sessões de treinamento"
        ]
      }
    ],
    "faq": [
      {
        "q": "Com que frequência ele é atualizado?",
        "a": "As atualizações são publicadas à medida que novos lançamentos são enviados."
      },
      {
        "q": "Onde posso solicitar recursos?",
        "a": "Compartilhe comentários dentro do aplicativo ou por meio da Central de Ajuda."
      }
    ]
  }
};

export const APP_ROUTE_SEO_UI_COPY_PT = {
  "home": "Início",
  "faq": "Perguntas frequentes",
  "summaryTitle": "DR",
  "sourcesTitle": "Fontes",
  "sourcesIntro": "As fontes desta página incluem as páginas oficiais de workflow, pricing e support da Stocktag vinculadas abaixo.",
  "relatedLinks": "Links relacionados",
  "workflowNotes": "Notas do fluxo de trabalho",
  "depthIntro": (title, keywordSummary) => `Esta página faz parte do fluxo de trabalho __pt_param_0__ completo do Stocktag para contribuidores que precisam de resultados confiáveis em escala. Ele se concentra em etapas práticas como ${keywordSummary}, para que as equipes possam publicar mais rapidamente sem diminuir a qualidade dos metadados.`,
  "depthBody": "Para obter resultados consistentes, emparelhe esta rota com Metadata Studio, mantenha a ordem das palavras-chave intencional e execute uma breve passagem de controle de qualidade antes da exportação. Esse processo ajuda a proteger a descoberta em Adobe Stock, Shutterstock e outros mercados.",
  "depthBullets": [
    "Use modelos repetíveis para títulos, descrições e palavras-chave.",
    "Valide a relevância e remova termos fracos ou duplicados antes de exportar.",
    "Acompanhe o desempenho e atualize arquivos mais antigos com melhor intenção de pesquisa."
  ]
};


export default APP_ROUTE_SEO_CONTENT_PT;
